import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import {
    Banner, Button, EmptyState, ErrorState, Segmented, SelectField, Skel, Slider, cx, useElementWidth, useIsMobile,
} from './components/ui';
import { GAMES, failMessage, readError, readJson, sentence } from './components/patient/patientData';
import './GameParameters.css';

/**
 * Game parameters for one patient — master–detail: game list + parameter rows.
 *
 * Data (enableportal api.settings.js):
 *   definitions  GET /api/settings/{game}                       → { game_id, data: { [param]: definition } }
 *   values       GET /api/settings?game_id&phase&user_id         → { name, user_id, game_id, phase, data: { [param]: value } }
 *   save         POST /api/settings { user_id, game_id, phase, data: JSON.stringify({ [param]: value | "" }) }
 *
 * The server stores a full value map per patient (seeded from the defaults), so an "override" is a key whose
 * stored value differs from the definition default — or one the provider moved to Override in this session.
 * Sending "" for a key resets it to the game default server-side.
 */

/* The only phase in use. The portal has never offered a phase choice, so none is shown. */
const PHASE = 'warmup';
const TYPES = new Set(['RangeParameter', 'BoolParameter', 'StringListParameter']);

/* ── Definitions & values ─────────────────────────────────────────────── */
const toBool = (v) => v === true || (typeof v === 'string' && v.toLowerCase() === 'true');

const decimals = (x) => {
    const s = String(x);
    const e = s.match(/e-(\d+)$/i);
    if (e) return Math.min(6, Number(e[1]));
    const i = s.indexOf('.');
    return i < 0 ? 0 : Math.min(6, s.length - i - 1);
};

/* Range definitions without a tick get a step proportional to their span. */
const fallbackStep = (span) => (span <= 2 ? 0.01 : span <= 20 ? 0.1 : 1);

function normalizeDefs(data) {
    const byKey = {};
    const order = [];
    if (data && typeof data === 'object') {
        Object.entries(data).forEach(([key, d]) => {
            if (!d || !TYPES.has(d.type)) return;
            const def = {
                key,
                type: d.type,
                label: String(d.alias || d.name || key).trim(),
                category: d.category || 'Other',
                description: d.description ? String(d.description).trim() : '',
                value: d.value,
            };
            if (d.type === 'RangeParameter') {
                const a = Number(d.min_);
                const b = Number(d.max_);
                let lo = Number.isFinite(a) ? a : 0;
                let hi = Number.isFinite(b) ? b : 100;
                if (lo > hi) [lo, hi] = [hi, lo]; // some definition files list min/max reversed
                const t = Number(d.tick_);
                def.lo = lo;
                def.hi = hi;
                def.step = Number.isFinite(t) && t > 0 ? t : fallbackStep(hi - lo);
                def.dp = Math.max(decimals(def.step), decimals(lo));
                const v = Number(d.value);
                def.value = Number.isFinite(v) ? v : lo;
            } else if (d.type === 'BoolParameter') {
                def.value = toBool(d.value);
            } else {
                def.strings = Array.isArray(d.strings) ? d.strings.map(String) : [];
                def.value = d.value == null ? def.strings[0] || '' : String(d.value);
            }
            byKey[key] = def;
            order.push(key);
        });
    }
    return { byKey, order };
}

/* Clamp to [min, max] and snap to the tick grid that starts at min. */
function snap(def, x) {
    const n = Math.round((x - def.lo) / def.step) * def.step + def.lo;
    return +Math.max(def.lo, Math.min(def.hi, n)).toFixed(def.dp);
}

function same(def, a, b) {
    if (def.type === 'RangeParameter') {
        const x = Number(a);
        const y = Number(b);
        return Number.isFinite(x) && Number.isFinite(y) ? Math.abs(x - y) < 1e-9 : String(a) === String(b);
    }
    if (def.type === 'BoolParameter') return toBool(a) === toBool(b);
    return String(a) === String(b);
}

function fromServer(def, v) {
    if (def.type === 'RangeParameter') {
        const n = Number(v);
        return Number.isFinite(n) ? n : def.value;
    }
    if (def.type === 'BoolParameter') return toBool(v);
    return String(v);
}

/* What the server currently holds, as override flags + values. */
function baseline(defs, server, pinned) {
    const out = {};
    defs.order.forEach((k) => {
        const def = defs.byKey[k];
        const has = !!server && Object.prototype.hasOwnProperty.call(server, k) && server[k] != null && server[k] !== '';
        const v = has ? fromServer(def, server[k]) : def.value;
        const o = (has && !same(def, v, def.value)) || pinned.has(k);
        out[k] = { o, v: o ? v : def.value };
    });
    return out;
}

const isDirty = (def, d, b) => !!d && !!b && (d.o !== b.o || (d.o && !same(def, d.v, b.v)));

function fmtValue(def, v) {
    if (def.type === 'BoolParameter') return toBool(v) ? 'On' : 'Off';
    if (def.type === 'RangeParameter') {
        const n = Number(v);
        if (!Number.isFinite(n)) return String(v ?? '—');
        if (def.dp > 0) return n.toFixed(def.dp);
        return Number.isInteger(n) ? String(n) : String(+n.toFixed(3));
    }
    return v == null || v === '' ? '—' : String(v);
}

/* A parameter's facts, from its definition: range and step, the switch, or its options (3 or fewer by name).
   The definitions carry float noise (tick_ 0.100000001490116), so figures keep 6 significant digits. */
const factNum = (n) => String(+Number(n).toPrecision(6)).replace('-', '−');
export function paramFacts(def) {
    if (def.type === 'RangeParameter') return `${factNum(def.lo)} – ${factNum(def.hi)} / step ${factNum(def.step)}`;
    if (def.type === 'BoolParameter') return 'On / Off';
    const n = (def.strings || []).length;
    if (!n) return null;
    return n <= 3 ? def.strings.join(' / ') : `${n} options`;
}

const toWire = (def, v) => (def.type === 'RangeParameter' ? Number(v) : def.type === 'BoolParameter' ? toBool(v) : String(v));
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/* ── Small pieces ─────────────────────────────────────────────────────── */
function UnsavedMark() {
    return (
        <span className="gp-unsaved" title="Unsaved change">
            <span className="eg-sr-only">Unsaved change</span>
        </span>
    );
}

const ResetGlyph = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" />
    </svg>
);

const UpGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true">
        <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
);

/** Number box beside the slider: free typing, committed (clamped + snapped) on blur / Enter. */
function NumberInput({ def, value, onChange }) {
    const [text, setText] = useState(fmtValue(def, value));
    const [focused, setFocused] = useState(false);
    useEffect(() => {
        if (!focused) setText(fmtValue(def, value));
    }, [def, value, focused]);
    const n = parseFloat(text);
    const bad = text.trim() !== '' && (!Number.isFinite(n) || n < def.lo || n > def.hi);
    const commit = () => {
        const x = parseFloat(text);
        if (Number.isFinite(x)) {
            const v = snap(def, x);
            onChange(v);
            setText(fmtValue(def, v));
        } else {
            setText(fmtValue(def, value));
        }
    };
    return (
        <input
            type="number"
            inputMode="decimal"
            className="eg-input eg-input--num gp-num"
            aria-label={`${def.label} value`}
            aria-invalid={bad || undefined}
            title={`${fmtValue(def, def.lo)} to ${fmtValue(def, def.hi)}, steps of ${def.step}`}
            min={def.lo}
            max={def.hi}
            step={def.step}
            value={text}
            onFocus={() => setFocused(true)}
            onBlur={() => { setFocused(false); commit(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
            onChange={(e) => {
                setText(e.target.value);
                const x = parseFloat(e.target.value);
                if (Number.isFinite(x) && x >= def.lo && x <= def.hi) onChange(snap(def, x));
            }}
        />
    );
}

function OverrideRow({ id, def, entry, dirty, onChange, onReset }) {
    const v = entry.v;
    let control = null;
    if (def.type === 'RangeParameter') {
        const num = Number.isFinite(Number(v)) ? Number(v) : def.value;
        const inRange = (x) => Math.max(def.lo, Math.min(def.hi, x));
        control = (
            <>
                <div className="gp-range">
                    <Slider
                        value={inRange(num)}
                        min={def.lo}
                        max={def.hi}
                        step={def.step}
                        label={def.label}
                        rail="var(--egdl-accent-rail)"
                        ticks={[{ value: inRange(def.value), label: `Default ${fmtValue(def, def.value)}` }]}
                        onChange={(x) => onChange(snap(def, x))}
                    />
                </div>
                <NumberInput def={def} value={num} onChange={onChange} />
            </>
        );
    } else if (def.type === 'BoolParameter') {
        control = (
            <Segmented
                label={def.label}
                value={toBool(v)}
                onChange={onChange}
                options={[{ value: true, label: 'On' }, { value: false, label: 'Off' }]}
            />
        );
    } else {
        const opts = def.strings.map((s) => ({ value: s, label: s }));
        if (!def.strings.includes(String(v))) opts.unshift({ value: String(v), label: `${v} (no longer offered)` });
        control = <SelectField ariaLabel={def.label} value={String(v)} onChange={onChange} options={opts} className="gp-grow" />;
    }
    return (
        <div id={id} className="gp-orow">
            <div className="gp-orow__text">
                <span className="gp-orow__label" title={def.description || undefined}>{def.label}{dirty ? <UnsavedMark /> : null}</span>
                {/* facts, not the description: the default, and a slider's ends and step */}
                <span className="gp-orow__note">
                    {[`Default ${fmtValue(def, def.value)}`, def.type === 'RangeParameter' ? paramFacts(def) : null].filter(Boolean).join(' / ')}
                </span>
            </div>
            <div className="gp-orow__ctl">{control}</div>
            <Button variant="ghost" muted onClick={onReset} aria-label={`Use default for ${def.label}`}>
                <ResetGlyph />Use default
            </Button>
        </div>
    );
}

function DefaultRow({ id, def, dirty, flash, onOverride }) {
    const facts = paramFacts(def);
    return (
        <div id={id} className={cx('gp-drow', flash && 'gp-drow--flash')}>
            <div className="gp-drow__name">
                <span className="gp-drow__line">
                    <span className="gp-drow__label" title={def.description || undefined}>{def.label}</span>
                    {dirty ? <UnsavedMark /> : null}
                </span>
                {facts ? <span className="gp-drow__spec">{facts}</span> : null}
            </div>
            <span className="gp-drow__value">{fmtValue(def, def.value)}</span>
            <Button variant="secondary" className="gp-ovr-btn" onClick={onOverride} aria-label={`Override ${def.label}`}>
                <UpGlyph />Override
            </Button>
        </div>
    );
}

function SaveStatus({ save, dirtyCount, savedAt }) {
    let s = null;
    if (save === 'saving') s = { tone: 'saving', text: 'Saving…' };
    else if (save === 'error') s = { tone: 'error', text: 'Not saved' };
    else if (dirtyCount) s = { tone: 'unsaved', text: plural(dirtyCount, 'unsaved change', 'unsaved changes') };
    else if (save === 'saved') {
        const t = savedAt ? new Date(savedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : 'just now';
        s = { tone: 'saved', text: `Saved · ${t}` };
    }
    return (
        <span role="status" aria-live="polite" className={cx('gp-status', s && `gp-status--${s.tone}`)}>
            {s ? <><span className="eg-dot" aria-hidden="true" /><span className="gp-status__text">{s.text}</span></> : null}
        </span>
    );
}

function DetailSkeleton() {
    return (
        <div aria-busy="true" className="gp-skel">
            <span className="eg-sr-only">Loading parameters…</span>
            <Skel h={56} w="40%" raised />
            <Skel h={76} raised style={{ marginTop: 32 }} />
            <Skel h={76} raised />
            <Skel h={64} raised style={{ marginTop: 40 }} />
            <Skel h={64} raised />
            <Skel h={64} raised />
        </div>
    );
}

/* ── Main ─────────────────────────────────────────────────────────────── */
const initialRecs = () => Object.fromEntries(GAMES.map((g) => [g.id, { status: 'loading', error: null }]));

export default function GameParameters({ patientId }) {
    const { authFetch } = useAuth();
    const isMobile = useIsMobile();
    const [rootRef, width] = useElementWidth();
    const stacked = isMobile || width < 720;

    const [recs, setRecs] = useState(initialRecs);
    const [current, setCurrent] = useState(GAMES[0].id);
    const [flash, setFlash] = useState({});
    const alive = useRef(true);
    const focusNext = useRef(null);
    useEffect(() => {
        alive.current = true; // StrictMode re-runs effects: re-arm after the simulated unmount
        return () => { alive.current = false; };
    }, []);

    const patch = useCallback((gameId, fn) => setRecs((m) => ({ ...m, [gameId]: fn(m[gameId]) })), []);

    const load = useCallback(async (gameId) => {
        patch(gameId, (r) => ({ ...r, status: 'loading', error: null }));
        try {
            const q = new URLSearchParams({ game_id: gameId, phase: PHASE, user_id: patientId });
            const [defRes, valRes] = await Promise.all([
                // GET /api/settings/:game is marked deprecated server-side ("use api/settings/all"), but /all only
                // returns stored values. This is still the only endpoint that returns parameter definitions
                // (type, category, alias, description, min_/max_/tick_, strings, default value) — keep it.
                authFetch(`${API_BASE}/api/settings/${encodeURIComponent(gameId)}`),
                authFetch(`${API_BASE}/api/settings?${q.toString()}`),
            ]);
            if (!defRes.ok) throw new Error(await readError(defRes));
            if (!valRes.ok) throw new Error(await readError(valRes));
            const defBody = await readJson(defRes);
            const valBody = await readJson(valRes);
            const defs = normalizeDefs(defBody?.data);
            const server = valBody?.data && typeof valBody.data === 'object' ? valBody.data : {};
            const pinned = new Set();
            if (!alive.current) return;
            patch(gameId, () => ({
                status: 'ready', error: null, defs, server, pinned,
                draft: baseline(defs, server, pinned), save: 'idle', saveError: null, savedAt: null,
            }));
        } catch (e) {
            if (!alive.current) return;
            patch(gameId, (r) => ({ ...r, status: 'error', error: failMessage(e) }));
        }
    }, [authFetch, patientId, patch]);

    useEffect(() => {
        GAMES.forEach((g) => load(g.id));
    }, [load]);

    /* Per-game summaries for the list, the select and the header. */
    const summaries = useMemo(() => {
        const out = {};
        GAMES.forEach((g) => {
            const r = recs[g.id];
            if (!r || r.status !== 'ready') {
                out[g.id] = { status: r?.status || 'loading', oCount: 0, dCount: 0, dirty: 0, flag: '' };
                return;
            }
            const base = baseline(r.defs, r.server, r.pinned);
            let oCount = 0;
            let dirty = 0;
            r.defs.order.forEach((k) => {
                if (r.draft[k]?.o) oCount += 1;
                if (isDirty(r.defs.byKey[k], r.draft[k], base[k])) dirty += 1;
            });
            const flag = r.save === 'error' ? 'Error' : dirty ? 'Unsaved' : '';
            out[g.id] = { status: 'ready', oCount, dCount: r.defs.order.length - oCount, dirty, flag, base };
        });
        return out;
    }, [recs]);

    /* Unsaved edits survive tab and game switches; warn before the page is closed or reloaded. */
    const anyDirty = Object.values(summaries).some((s) => s.dirty > 0);
    useEffect(() => {
        if (!anyDirty) return undefined;
        const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [anyDirty]);

    /* Move focus to the row a parameter just moved to. */
    useEffect(() => {
        if (!focusNext.current) return;
        const el = document.getElementById(focusNext.current);
        focusNext.current = null;
        el?.querySelector('[role="slider"], [role="radio"][tabindex="0"], select, input, button')?.focus();
    });

    const setEntry = (gameId, key, entry) => patch(gameId, (r) => ({
        ...r,
        draft: { ...r.draft, [key]: { ...r.draft[key], ...entry } },
        save: r.save === 'saving' ? 'saving' : 'idle',
    }));

    const rowId = (gameId, key, kind) => `gp-${gameId}-${recs[gameId].defs.order.indexOf(key)}-${kind}`;

    const override = (gameId, key) => {
        const def = recs[gameId].defs.byKey[key];
        focusNext.current = rowId(gameId, key, 'o');
        setEntry(gameId, key, { o: true, v: def.value });
    };

    const resetToDefault = (gameId, key) => {
        const def = recs[gameId].defs.byKey[key];
        focusNext.current = rowId(gameId, key, 'd');
        setEntry(gameId, key, { o: false, v: def.value });
        const fk = `${gameId}:${key}`;
        setFlash((f) => ({ ...f, [fk]: true }));
        setTimeout(() => { if (alive.current) setFlash((f) => ({ ...f, [fk]: false })); }, 900);
    };

    const discard = (gameId) => patch(gameId, (r) => ({ ...r, draft: baseline(r.defs, r.server, r.pinned), save: 'idle', saveError: null }));

    const save = async (gameId) => {
        const r = recs[gameId];
        if (!r || r.status !== 'ready' || r.save === 'saving') return;
        const base = baseline(r.defs, r.server, r.pinned);
        const changes = {};
        r.defs.order.forEach((k) => {
            const def = r.defs.byKey[k];
            if (!isDirty(def, r.draft[k], base[k])) return;
            // "" asks the server to reset the key to the game default.
            changes[k] = r.draft[k].o ? toWire(def, r.draft[k].v) : '';
        });
        if (!Object.keys(changes).length) return;
        const sent = r.draft;
        patch(gameId, (x) => ({ ...x, save: 'saving', saveError: null }));
        try {
            const res = await authFetch(`${API_BASE}/api/settings`, {
                method: 'POST',
                body: JSON.stringify({ user_id: patientId, game_id: gameId, phase: PHASE, data: JSON.stringify(changes) }),
            });
            if (!res.ok) throw new Error(await readError(res));
            const body = await readJson(res).catch(() => null);
            if (!alive.current) return;
            patch(gameId, (x) => {
                let server = body?.data && typeof body.data === 'object' ? body.data : null;
                if (!server) {
                    server = { ...x.server };
                    Object.entries(changes).forEach(([k, v]) => { server[k] = v === '' ? x.defs.byKey[k].value : v; });
                }
                const pinned = new Set(x.pinned);
                Object.entries(changes).forEach(([k, v]) => { if (v === '') pinned.delete(k); else pinned.add(k); });
                const nextBase = baseline(x.defs, server, pinned);
                return {
                    ...x, server, pinned, save: 'saved', saveError: null, savedAt: Date.now(),
                    // Keep edits made while the request was in flight.
                    draft: x.draft === sent ? nextBase : x.draft,
                };
            });
        } catch (e) {
            if (!alive.current) return;
            patch(gameId, (x) => ({ ...x, save: 'error', saveError: failMessage(e) }));
        }
    };

    /* ── Render ── */
    const game = GAMES.find((g) => g.id === current) || GAMES[0];
    const rec = recs[game.id];
    const sum = summaries[game.id];

    const caption = (s) => {
        if (s.status === 'loading') return 'Loading…';
        if (s.status === 'error') return 'Didn’t load';
        return s.oCount ? plural(s.oCount, 'override', 'overrides') : 'Defaults only';
    };

    const list = stacked ? null : (
        <nav aria-label="Games" className="gp-list">
            {GAMES.map((g) => {
                const s = summaries[g.id];
                const on = g.id === game.id;
                return (
                    <button key={g.id} type="button" className="gp-list__item" aria-current={on ? 'true' : undefined} onClick={() => setCurrent(g.id)}>
                        <span className="gp-list__text">
                            <span className="gp-list__name">{g.label}</span>
                            <span className={cx('gp-list__cap', s.oCount > 0 && 'gp-list__cap--on', s.status === 'error' && 'gp-list__cap--err')}>{caption(s)}</span>
                        </span>
                        {s.flag ? (
                            <span className={cx('gp-flag', s.flag === 'Error' && 'gp-flag--error')}>
                                <span className="eg-dot" aria-hidden="true" />{s.flag}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </nav>
    );

    const picker = stacked ? (
        <SelectField
            label="Game"
            className="gp-mselect"
            value={game.id}
            onChange={setCurrent}
            options={GAMES.map((g) => {
                const s = summaries[g.id];
                const bits = [g.label];
                if (s.oCount) bits.push(plural(s.oCount, 'override', 'overrides'));
                if (s.status === 'error') bits.push('didn’t load');
                if (s.flag) bits.push(s.flag.toLowerCase());
                return { value: g.id, label: bits.join(' · ') };
            })}
        />
    ) : null;

    let body;
    if (!rec || rec.status === 'loading') {
        body = <DetailSkeleton />;
    } else if (rec.status === 'error') {
        body = (
            <ErrorState title={`${game.label} parameters didn’t load`} onRetry={() => load(game.id)}>
                {sentence(rec.error || 'The server returned an error')} Other games are unaffected.
            </ErrorState>
        );
    } else {
        const { defs, draft } = rec;
        const base = sum.base;
        const overrides = defs.order.filter((k) => draft[k]?.o);
        const cats = [];
        defs.order.forEach((k) => {
            if (draft[k]?.o) return;
            const def = defs.byKey[k];
            let c = cats.find((x) => x.name === def.category);
            if (!c) { c = { name: def.category, keys: [] }; cats.push(c); }
            c.keys.push(k);
        });
        const canSave = sum.dirty > 0 && rec.save !== 'saving';
        const saveLabel = rec.save === 'saving' ? 'Saving…' : stacked ? 'Save' : `Save ${game.label}`;

        body = (
            <>
                <div className="gp-head">
                    <div className="gp-head__id">
                        <h2 className="gp-head__name">{game.label}</h2>
                        <span className="gp-head__sum">
                            {sum.oCount} overridden · {sum.dCount} at default · applies from the next session
                        </span>
                    </div>
                    <SaveStatus save={rec.save} dirtyCount={sum.dirty} savedAt={rec.savedAt} />
                    <div className="gp-head__actions">
                        {sum.dirty > 0 && rec.save !== 'saving' ? (
                            <Button variant="ghost" muted onClick={() => discard(game.id)}>Discard</Button>
                        ) : null}
                        <Button variant="primary" size="sm" onClick={() => save(game.id)} disabled={!canSave}>{saveLabel}</Button>
                    </div>
                </div>

                {rec.save === 'error' ? (
                    <div className="gp-banner">
                        <Banner
                            tone="danger"
                            title={`Couldn’t save ${game.label}.`}
                            action={<Button variant="secondary" onClick={() => save(game.id)} disabled={!canSave}>Retry save</Button>}
                        >
                            {sentence(rec.saveError || 'The server didn’t respond')} Your changes are kept here — try again.
                        </Banner>
                    </div>
                ) : null}

                {!defs.order.length ? (
                    <div className="gp-gap">
                        <EmptyState title="No parameters" />
                    </div>
                ) : (
                    <>
                        <div className="gp-sechead">
                            <span className="gp-sechead__title gp-sechead__title--accent">Overrides for this patient</span>
                            <span className="gp-sechead__count">{overrides.length}</span>
                        </div>
                        <div className="gp-olist">
                            {overrides.map((k) => (
                                <OverrideRow
                                    key={k}
                                    id={rowId(game.id, k, 'o')}
                                    def={defs.byKey[k]}
                                    entry={draft[k]}
                                    dirty={isDirty(defs.byKey[k], draft[k], base[k])}
                                    onChange={(v) => setEntry(game.id, k, { v })}
                                    onReset={() => resetToDefault(game.id, k)}
                                />
                            ))}
                            {!overrides.length ? (
                                <div className="gp-none">No overrides</div>
                            ) : null}
                        </div>

                        <div className="gp-sechead gp-sechead--defaults">
                            <span className="gp-sechead__title">Game defaults</span>
                            <span className="gp-sechead__count">{sum.dCount}</span>
                        </div>
                        {cats.map((c) => (
                            <div key={c.name} className="gp-cat">
                                <span className="gp-cat__name">{c.name}</span>
                                {c.keys.map((k) => (
                                    <DefaultRow
                                        key={k}
                                        id={rowId(game.id, k, 'd')}
                                        def={defs.byKey[k]}
                                        dirty={isDirty(defs.byKey[k], draft[k], base[k])}
                                        flash={!!flash[`${game.id}:${k}`]}
                                        onOverride={() => override(game.id, k)}
                                    />
                                ))}
                            </div>
                        ))}
                        {!cats.length ? <p className="gp-alldone">Every parameter is overridden for this patient.</p> : null}
                    </>
                )}
            </>
        );
    }

    return (
        <div ref={rootRef} className={cx('gp', stacked && 'gp--stacked')}>
            {list}
            <section aria-label={`${game.label} parameters`} className="gp-detail">
                {picker}
                {body}
            </section>
        </div>
    );
}
